import React from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors, fontSize, margins } from "style";
import { createMarkup } from "utils";
import { CHALLENGE_ITEM_TYPES } from "constants/index";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";

/**
 * ChallengeItemDetailView component displays the details of a selected challenge item.
 *
 * @param {Object}    activeItem - The currently selected challenge item.
 * @param {Number}    activeItemIndex - The index of the currently selected challenge item.
 * @param {Number}    totalItemsCount - The total number of challenge items.
 * @param {Function}  handlePrev - Function to navigate to the previous challenge item.
 * @param {Function}  handleNext - Function to navigate to the next challenge item.
 */
const ChallengeItemDetailView = ({ activeItem, activeItemIndex, totalItemsCount, handlePrev, handleNext }) => {
  if (!activeItem) {
    return (
      <div css={styles.detailPane}>
        <div css={styles.emptyState}>
          <FormattedMessage id="CodeChallenges.ChallengeItemDetailView.NoItemSelected" />
        </div>
      </div>
    );
  }

  return (
    <div css={styles.detailPane}>
      <div css={styles.detailHeader}>
        <div css={styles.detailHeaderLeft}>
          <h1 css={styles.detailMainTitle} dangerouslySetInnerHTML={createMarkup(activeItem.title)} />
          <div css={styles.badgeRow}>
            {activeItem.metadata?.language && (
              <span css={[styles.badge, styles.langBadge]}>{activeItem.metadata.language}</span>
            )}
            {activeItem.metadata?.tags?.map((tag) => (
              <span key={tag} css={[styles.badge, styles.tagBadge]}>
                {tag}
              </span>
            ))}
            {activeItem.metadata?.difficulty && (
              <span css={[styles.badge, styles.diffBadge]}>{activeItem.metadata.difficulty}</span>
            )}
          </div>
        </div>
        <div css={styles.paginationControls}>
          <span css={styles.paginationText}>
            <FormattedMessage
              id="CodeChallenges.ChallengeItemDetailView.ItemPagination"
              values={{
                current: activeItemIndex + 1,
                total: totalItemsCount,
              }}
            />
          </span>
          <div css={styles.navButtonWrapper}>
            <button type="button" onClick={handlePrev} disabled={activeItemIndex === 0} css={styles.navBtn}>
              <Icon type={ICON_TYPE.chevronLeft} size={ICON_SIZE.small} color={colors.grayAnatomyDark} />
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={activeItemIndex === totalItemsCount - 1}
              css={styles.navBtn}
            >
              <Icon type={ICON_TYPE.chevronRight} size={ICON_SIZE.small} color={colors.grayAnatomyDark} />
            </button>
          </div>
        </div>
      </div>
      {activeItem.summary && (
        <div css={styles.summaryContainer}>
          <strong css={styles.summaryLabel}>
            <FormattedMessage id="CodeChallenges.ChallengeItemDetailView.SummaryLabel" />
          </strong>
          <p css={styles.summaryDesc}>{activeItem.summary}</p>
        </div>
      )}
      <div css={styles.detailScrollContent}>
        {activeItem.instructions && (
          <div css={styles.instructionBlock}>
            <h3 css={styles.sectionHeading}>
              <Icon type={ICON_TYPE.listLines} size={ICON_SIZE.small} color={colors.purpleRainBase} />
              <FormattedMessage id="CodeChallenges.ChallengeItemDetailView.Instructions" />
            </h3>
            <div css={styles.htmlRenderer} dangerouslySetInnerHTML={createMarkup(activeItem.instructions)} />
          </div>
        )}
        {activeItem.type === CHALLENGE_ITEM_TYPES.MCQ && activeItem.metadata?.answers && (
          <div css={styles.mcqBlock}>
            <h3 css={styles.sectionHeading}>
              <FormattedMessage id="CodeChallenges.ChallengeItemDetailView.Options" />
            </h3>
            <div css={styles.optionsWrapper}>
              {activeItem.metadata.answers.map((answer, i) => (
                <div key={answer.service_id || i} css={styles.optionRow}>
                  <span css={styles.optionLetter}>{String.fromCharCode(65 + i)}</span>
                  <span css={styles.optionText} dangerouslySetInnerHTML={createMarkup(answer.text)} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ChallengeItemDetailView.propTypes = {
  activeItem: PropTypes.object,
  activeItemIndex: PropTypes.number.isRequired,
  totalItemsCount: PropTypes.number.isRequired,
  handlePrev: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};

// Co-located Detail Pane Styles
const styles = {
  detailPane: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: ${colors.white};
  `,
  detailHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 12px 24px 24px 24px;
    border-bottom: 1px solid ${colors.grayAnatomyLight4};
    gap: 16px;
  `,
  detailHeaderLeft: css`
    flex: 1;
    min-width: 0;
  `,
  detailMainTitle: css`
    font-size: ${fontSize.large};
    font-weight: 700;
    color: ${colors.grayAnatomyDark};
    margin: 0 0 12px 0;
    p {
      margin: 0;
    }
  `,
  badgeRow: css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `,
  badge: css`
    font-size: ${fontSize.xxsmall};
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 12px;
  `,
  langBadge: css`
    background-color: ${colors.purpleRainLight8};
    color: ${colors.purpleRainDark1};
  `,
  tagBadge: css`
    background-color: ${colors.blueVelvetLight2};
    color: ${colors.grayAnatomyDark};
  `,
  diffBadge: css`
    background-color: ${colors.grayAnatomyLight4};
    color: ${colors.grayAnatomyBase};
  `,
  paginationControls: css`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: 16px;
  `,
  paginationText: css`
    font-size: ${fontSize.xxsmall};
    color: ${colors.grayAnatomyBase};
    font-weight: 500;
    white-space: nowrap;
  `,
  navButtonWrapper: css`
    display: flex;
    gap: 4px;
  `,
  navBtn: css`
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${colors.grayAnatomyLight3};
    background-color: ${colors.white};
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  `,
  summaryContainer: css`
    margin: 0 24px;
    background-color: ${colors.purpleRainLight9};
    border: 1px solid ${colors.purpleRainLight7};
    padding: 16px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: ${margins.xxsmall};
  `,
  summaryLabel: css`
    font-size: ${fontSize.xxxsmall};
    color: ${colors.purpleRainBase};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `,
  summaryDesc: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyBase};
    margin: 0;
  `,
  detailScrollContent: css`
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  `,
  sectionHeading: css`
    font-size: ${fontSize.xxsmall};
    font-weight: 700;
    color: ${colors.purpleRainBase};
    letter-spacing: 0.5px;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 6px;
    text-transform: uppercase;
  `,
  instructionBlock: css`
    display: flex;
    flex-direction: column;
  `,
  htmlRenderer: css`
    font-size: ${fontSize.small};
    color: ${colors.grayAnatomyDark};
    line-height: 1.6;

    ul,
    ol {
      padding-left: 20px;
      margin-bottom: 16px;
    }

    li {
      margin-bottom: 6px;
    }

    pre {
      background-color: ${colors.grayAnatomyLight7};
      border: 1px solid ${colors.grayAnatomyLight3};
      color: ${colors.grayAnatomyDark};
      padding: 14px;
      border-radius: 8px;
      overflow-x: auto;
      font-size: ${fontSize.xxsmall};
    }
  `,
  apiBlock: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: ${colors.grayAnatomyLight5};
    border: 1px solid ${colors.grayAnatomyLight3};
    padding: 12px 16px;
    border-radius: 8px;
  `,
  apiTitle: css`
    font-size: ${fontSize.xxxsmall};
    font-weight: 700;
    color: ${colors.grayAnatomyBase};
    text-transform: uppercase;
    margin: 0;
  `,
  apiInputGroup: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${colors.white};
    border: 1px solid ${colors.grayAnatomyLight3};
    padding: 8px 12px;
    border-radius: 6px;
  `,
  apiUrlText: css`
    font-family: monospace;
    font-size: ${fontSize.xxsmall};
    color: ${colors.purpleRainBase};
    word-break: break-all;
  `,
  copyBtn: css`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    &:hover {
      background-color: ${colors.grayAnatomyLight4};
    }
  `,
  mcqBlock: css`
    border-top: 1px dashed ${colors.grayAnatomyLight3};
    padding-top: 24px;
  `,
  optionsWrapper: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  optionRow: css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border: 1px solid ${colors.grayAnatomyLight4};
    background-color: ${colors.grayAnatomyLight7};
    border-radius: 6px;
    font-size: ${fontSize.xsmall};
    p {
      margin: 0;
    }
  `,
  optionLetter: css`
    font-weight: 700;
    background-color: ${colors.grayAnatomyLight4};
    color: ${colors.grayAnatomyBase};
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  `,
  optionText: css`
    color: ${colors.grayAnatomyDark};
  `,
  emptyState: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${colors.grayAnatomyLight1};
  `,
};

export default ChallengeItemDetailView;
